import { useEffect, useState } from 'react'

declare global {
	interface Window {
		dataLayer: unknown[]
	}
}

export interface ConsentState {
	necessary: true
	analytics: boolean
	advertising: boolean
	decided: boolean
	timestamp: number | null
}

const STORAGE_KEY = 'haw_cookie_consent'

// Bump this date whenever the cookie policy changes — any stored consent older than this is expired
const CONSENT_CUTOFF = new Date('2026-05-27').getTime()

const DEFAULT_CONSENT: ConsentState = {
	necessary: true,
	analytics: false,
	advertising: false,
	decided: false,
	timestamp: null
}

// Replaced at build time by Gatsby — empty string means the container is not configured
const GTM_ANALYTICS_ID = process.env.GATSBY_GTM_ANALYTICS_ID ?? ''
const GTM_ADVERTISING_ID = process.env.GATSBY_GTM_ADVERTISING_ID ?? ''

const injectGTM = (id: string) => {
	if (!id || typeof document === 'undefined') return
	if (document.querySelector(`script[data-gtm-id="${id}"]`)) return
	window.dataLayer = window.dataLayer || []
	window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
	const script = document.createElement('script')
	script.async = true
	script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`
	script.setAttribute('data-gtm-id', id)
	document.head.appendChild(script)
}

/**
 * @hook useCookieConsent
 * @description Manages cookie consent state via localStorage. Injects the analytics and
 * advertising GTM containers dynamically — only after the user grants consent for each.
 */
const useCookieConsent = () => {
	const [consent, setConsent] = useState<ConsentState>(() => {
		if (typeof window === 'undefined') return DEFAULT_CONSENT
		try {
			const raw = localStorage.getItem(STORAGE_KEY)
			if (!raw) return DEFAULT_CONSENT
			const stored = JSON.parse(raw) as Partial<ConsentState>
			if (!stored.timestamp || stored.timestamp < CONSENT_CUTOFF) {
				localStorage.removeItem(STORAGE_KEY)
				return DEFAULT_CONSENT
			}
			return { ...DEFAULT_CONSENT, ...stored }
		} catch {
			return DEFAULT_CONSENT
		}
	})

	// On client mount, restore previously granted GTM containers
	useEffect(() => {
		try {
			const raw = localStorage.getItem(STORAGE_KEY)
			if (!raw) return
			const stored = JSON.parse(raw) as ConsentState
			if (!stored.decided) return
			if (!stored.timestamp || stored.timestamp < CONSENT_CUTOFF) {
				localStorage.removeItem(STORAGE_KEY)
				return
			}
			if (stored.analytics) injectGTM(GTM_ANALYTICS_ID)
			if (stored.advertising) injectGTM(GTM_ADVERTISING_ID)
		} catch {}
	}, [])

	const save = (next: ConsentState) => {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
		setConsent(next)
		if (next.analytics) injectGTM(GTM_ANALYTICS_ID)
		if (next.advertising) injectGTM(GTM_ADVERTISING_ID)
	}

	return {
		consent,
		acceptAll: () =>
			save({ necessary: true, analytics: true, advertising: true, decided: true, timestamp: Date.now() }),
		saveCustom: (analytics: boolean, advertising: boolean) =>
			save({ necessary: true, analytics, advertising, decided: true, timestamp: Date.now() })
	}
}

export default useCookieConsent
